import classNames from "classnames";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Portal } from "react-overlays";
import { usePopper } from "react-popper";
import { overlaysRef } from "src/App";
import useOnClickOutside from "src/app/methods/useOnClickOutside";
import { DROPDOWN_OPEN_EVENT } from "src/utils/dropdownEvents";
import zIndex from "src/utils/zIndex";

export const DropdownPopper = forwardRef(
  (
    {
      overlay,
      placement = "bottom-start",
      children,
      disableGlobalCloseEvent = false,
    },
    ref = null,
  ) => {
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
      placement,
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 4],
          },
        },
      ],
    });

    const popperContainer = useRef(null);
    const dropdownIdRef = useRef(Math.random().toString(36).slice(2));

    const [isOpen, setIsOpen] = useState(false);

    useOnClickOutside(popperContainer, () => {
      if (isOpen) {
        setIsOpen(false);
      }
    });

    useEffect(() => {
      if (disableGlobalCloseEvent) return;

      const handleOtherDropdownOpen = (event) => {
        if (event.detail?.id === dropdownIdRef.current) return;

        setIsOpen(false);
      };

      window.addEventListener(DROPDOWN_OPEN_EVENT, handleOtherDropdownOpen);

      return () => {
        window.removeEventListener(
          DROPDOWN_OPEN_EVENT,
          handleOtherDropdownOpen,
        );
      };
    }, [disableGlobalCloseEvent]);

    useEffect(() => {
      if (!isOpen || disableGlobalCloseEvent) return;

      window.dispatchEvent(
        new CustomEvent(DROPDOWN_OPEN_EVENT, {
          detail: { id: dropdownIdRef.current },
        }),
      );
    }, [isOpen, disableGlobalCloseEvent]);

    useImperativeHandle(ref, () => ({
      forceVisible(state) {
        setIsOpen(state);
      },
    }));

    return (
      <>
        <div
          ref={setReferenceElement}
          onClick={() => setIsOpen(!isOpen)}
          className={classNames([
            "dropdown-popper",
            isOpen && "dropdown-popper--open",
          ])}
        >
          {children}
        </div>

        <Portal container={overlaysRef}>
          {isOpen && (
            <div ref={popperContainer}>
              <div
                ref={setPopperElement}
                style={{ ...styles.popper, zIndex: zIndex.dropdown }}
                {...attributes.popper}
              >
                {overlay}
              </div>
            </div>
          )}
        </Portal>
      </>
    );
  },
);
